import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Col, Row } from "react-styled-flexboxgrid";
import { Text, Button } from "../theme";
import IntersectionVisible from "react-intersection-visible";
import { FaDownload } from "react-icons/fa";

const sanitizeFilename = (value) =>
  (value || "meme")
    .toString()
    .trim()
    .replace(/[^a-z0-9-_]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "meme";

const getImageExtension = (url, contentType) => {
  const typeExtension = contentType?.split("/")[1]?.split(";")[0];
  if (typeExtension) {
    return typeExtension === "jpeg" ? "jpg" : typeExtension;
  }

  const path = new URL(url).pathname;
  const extension = path.split(".").pop();
  return extension && extension !== path ? extension : "jpg";
};

const MemeViewer = ({
  amount,
  list,
  more,
  responsive,
  action,
  id,
  tags,
  auto,
}) => {
  const [settings, memes] = useSelector((state) => [
    state.data.settings,
    state.data.memes,
  ]);

  const [prevList, setPrevList] = useState(list);
  const [selection, setSelection] = useState(null);
  const [target, setTarget] = useState(null);
  const [targetAmount, setTargetAmount] = useState(amount || 3);
  const myRef = useRef(null);

  const awardPriority = {
    winner: 1,
    second: 2,
    third: 3,
    featured: 4,
    none: 5,
    undefined: 6,
  };

  // console.log(list);

  const adder = 3;

  const downloadMeme = async ({ description, edition, img, _id }) => {
    try {
      const response = await fetch(img);
      if (!response.ok) {
        throw new Error(`Could not download ${img}`);
      }

      const blob = await response.blob();
      const extension = getImageExtension(
        img,
        response.headers.get("content-type")
      );
      const descriptionPart = sanitizeFilename(description || _id);
      const editionPart = edition ? `-${sanitizeFilename(edition)}` : "";
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${descriptionPart}${editionPart}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      window.alert("Could not download this meme. Please try again.");
    }
  };

  const getMeme = (_ref) => {
    let rv = 0;
    memes.forEach((m, i) => {
      if (_ref == m._id) {
        rv = i;
      }
    });
    return rv;
  };

  const populate = () => {
    let newlist = selection ? [...selection] : [];

    if (target && myRef) {
      // setTimeout(() => {
      //   if (myRef && myRef.current) {
      //     myRef.current.scrollIntoView({
      //       behavior: "smooth",
      //     });
      //   }
      // }, 200);
      setTarget(null);
    }

    if (list) {
      // Sort based on award priority before slicing
      const sorted = [...list].sort((a, b) => {
        const aIdx = getMeme(a._ref);
        const bIdx = getMeme(b._ref);
        const aAward = memes[aIdx]?.award || "none";
        const bAward = memes[bIdx]?.award || "none";
        return awardPriority[aAward] - awardPriority[bAward];
      });

      newlist = sorted
        .slice(0, Math.min(targetAmount, list.length))
        .map(({ _ref }) => getMeme(_ref));

      setTarget(newlist[newlist.length - adder]);
    } else {
      let setFirst = false;
      while (newlist.length < targetAmount) {
        const newindex = Math.floor(Math.random() * memes.length);
        if (
          !newlist.includes(newindex) &&
          memes[newindex] &&
          memes[newindex].display
        ) {
          newlist.push(newindex);
          if (!setFirst) {
            setTarget(newindex);
            setFirst = true;
          }
        }
      }
    }

    setSelection(newlist);
  };

  useEffect(() => {
    if (memes && !selection) {
      populate();
    } else if (list && list !== prevList) {
      setTargetAmount(amount);
      setPrevList(list);
      populate();
      setTarget(null);
    } else if (selection && selection.length < targetAmount) {
      if (list && list.length < targetAmount) {
        // populate()
      } else {
        populate();
      }
    }
  });

  if (!settings || !memes || !selection) {
    return null;
  }

  return (
    <Row>
      {selection.map((idx) => {
        const { author, description, edition, email, img, _id } = memes[idx];

        if (!img) {
          return null;
        }

        return (
          <Col
            key={_id}
            xs={12}
            sm={responsive ? 6 : 12}
            md={responsive ? 4 : 12}
            lg={responsive ? 4 : 12}
            ref={idx == target ? myRef : null}
          >
            <div className="MemeCard">
              <button
                className="MemeDownloadButton"
                type="button"
                aria-label="Download meme"
                title="Download meme"
                onClick={() => downloadMeme(memes[idx])}
              >
                <FaDownload />
              </button>
              <img className="ResponsiveImage" src={img} />
              <Text inverted>
                {description ? description : null}{" "}
                {author ? <>- {author}</> : null} <br />
                <i>{edition ? <> {edition} edition </> : null}</i>
              </Text>
              {action ? (
                <Button
                  invert
                  text="toggle display"
                  noUrl
                  action={() => {
                    action(memes[idx]);
                  }}
                />
              ) : null}
              {id ? (
                <Button
                  text="edit"
                  invert
                  url={`https://studio.massiveexoplanetmemeexhibition.com/desk/meme;${_id}`}
                  external
                />
              ) : null}
            </div>
          </Col>
        );
      })}
      <Col xs={12}>
        {more ? (
          <Button
            text="More"
            noUrl
            action={() => {
              setTargetAmount(targetAmount + adder);
            }}
          />
        ) : null}

        {auto ? (
          <IntersectionVisible
            onShow={() => {
              setTargetAmount(targetAmount + adder);
            }}
          >
            <div>&nbsp;</div>
          </IntersectionVisible>
        ) : null}
      </Col>
    </Row>
  );
};

export default MemeViewer;
